/**
 * ZAXIA Housekeeping Daily Service Planner
 *
 * Phase P2.3-1: Daily Service Planner
 *
 * Aggregates all data sources (Green Stay, Guest Requests, Maintenance, Room Status)
 * into a single, deterministic daily service plan for housekeeping operations.
 *
 * PRIORITY ORDER (highest to lowest):
 * 1. OUT_OF_ORDER - Room unavailable due to maintenance
 * 2. NO_SERVICE - Guest explicitly skipped cleaning
 * 3. TOWELS_ONLY - Guest requested towels refresh only
 * 4. FULL_CLEAN - Standard housekeeping (default)
 */

import type { EcoEvent } from './greenStay';
import { getAllEcoEvents } from './ecoLogger';
import type { ApiGuestRequest } from '@/api';

// =============================================================================
// TYPES
// =============================================================================

export type ServiceMode = 'OUT_OF_ORDER' | 'NO_SERVICE' | 'TOWELS_ONLY' | 'FULL_CLEAN' | 'CHECKOUT_TODAY';

export type TaskPriority = 'HIGH' | 'MEDIUM' | 'LOW';

export type TaskSource =
  | 'MAINTENANCE'
  | 'GREEN_STAY'
  | 'GUEST_REQUEST'
  | 'DEFAULT';

export interface RoomServicePlan {
  roomNumber: string;
  guestName?: string; // Phase P2.4-6: Guest name from PMS
  date: string;
  serviceMode: ServiceMode;
  priority: TaskPriority;
  source: TaskSource;
  notes: string[];
}

export interface HousekeepingTask {
  id: string;
  roomNumber: string;
  guestName?: string; // Phase P2.4-6: Guest name from PMS
  date: string;
  type: 'FULL_CLEAN' | 'TOWELS_ONLY' | 'SKIPPED'; // Added SKIPPED type
  status?: 'PENDING' | 'ASSIGNED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'BLOCKED'; // Phase P2.5-1: Task status
  priority: TaskPriority;
  source: TaskSource;
  estimatedDuration: number; // minutes
  greenStayEnabled?: boolean;
  notes?: string;
}

export interface WorkloadSummary {
  fullClean: number;
  towelsOnly: number;
  skip: number;
  outOfOrder: number;
  checkoutToday: number;
  total: number;
}

export interface DailyServicePlan {
  date: string;
  rooms: RoomServicePlan[];
  tasks: HousekeepingTask[];
  workload: WorkloadSummary;
  generatedAt: Date;
  isTomorrow?: boolean;
}

// =============================================================================
// PRIORITY CONSTANTS
// =============================================================================

const SERVICE_MODE_PRIORITY: Record<ServiceMode, number> = {
  OUT_OF_ORDER: 5,
  CHECKOUT_TODAY: 4,
  NO_SERVICE: 3,
  TOWELS_ONLY: 2,
  FULL_CLEAN: 1,
};

// Task durations (minutes)
const TASK_DURATION: Record<'FULL_CLEAN' | 'TOWELS_ONLY', number> = {
  FULL_CLEAN: 20,
  TOWELS_ONLY: 15,
};

// =============================================================================
// CORE PLANNER
// =============================================================================

/**
 * Generate complete daily service plan for specified date
 */
export function generateDailyServicePlan(
  targetDate: string,
  rooms: string[],
  guestRequests: ApiGuestRequest[] = []
): DailyServicePlan {
  console.log('[HousekeepingPlanner] Generating plan for:', targetDate, 'rooms:', rooms.length);

  const ecoEvents = getAllEcoEvents();
  const roomPlans: RoomServicePlan[] = [];

  // Generate plan for each room
  for (const roomNumber of rooms) {
    const plan = resolveRoomServicePlan(
      roomNumber,
      targetDate,
      ecoEvents,
      guestRequests
    );
    roomPlans.push(plan);
  }

  // Generate tasks from plans
  const tasks = generateTasksFromPlans(roomPlans);

  // Calculate workload
  const workload = calculateWorkload(roomPlans);

  const plan: DailyServicePlan = {
    date: targetDate,
    rooms: roomPlans,
    tasks,
    workload,
    generatedAt: new Date(),
  };

  console.log('[HousekeepingPlanner] Plan generated:', {
    rooms: plan.rooms.length,
    tasks: plan.tasks.length,
    workload: plan.workload,
  });

  return plan;
}

/**
 * Resolve service plan for a single room
 * Applies priority-based resolution rules
 */
function resolveRoomServicePlan(
  roomNumber: string,
  targetDate: string,
  ecoEvents: EcoEvent[],
  guestRequests: ApiGuestRequest[]
): RoomServicePlan {
  const candidates: Array<{
    serviceMode: ServiceMode;
    priority: number;
    source: TaskSource;
    note: string;
  }> = [];

  // Check for maintenance requests (highest priority)
  const maintenanceRequest = guestRequests.find(
    r => r.roomNumber === roomNumber &&
         r.category === 'MAINTENANCE' &&
         r.status !== 'COMPLETED' &&
         isRequestRelevant(r, targetDate)
  );

  if (maintenanceRequest) {
    candidates.push({
      serviceMode: 'OUT_OF_ORDER',
      priority: SERVICE_MODE_PRIORITY.OUT_OF_ORDER,
      source: 'MAINTENANCE',
      note: `Maintenance required: ${maintenanceRequest.description}`,
    });
  }

  // Check for Green Stay preferences
  const roomEcoEvents = ecoEvents.filter(
    e => e.roomNumber === roomNumber && e.date === targetDate
  );

  console.log(`[HousekeepingPlanner] Room ${roomNumber} on ${targetDate}:`, {
    totalEcoEvents: ecoEvents.length,
    roomEcoEvents: roomEcoEvents.length,
    events: roomEcoEvents.map(e => ({
      date: e.date,
      serviceMode: e.serviceMode,
      action: e.action,
      createdAt: e.createdAt
    }))
  });

  if (roomEcoEvents.length > 0) {
    // Sort by creation time (most recent first)
    const sortedEvents = [...roomEcoEvents].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    const latestEvent = sortedEvents[0];

    console.log(`[HousekeepingPlanner] Room ${roomNumber} latest event:`, {
      action: latestEvent?.action,
      serviceMode: latestEvent?.serviceMode,
      createdAt: latestEvent?.createdAt
    });

    if (!latestEvent) {
      // Should not happen, but guard against it
      console.warn('[HousekeepingPlanner] No latest event found for room:', roomNumber);
    } else {
      // Map eco event to service mode
      if (latestEvent.action === 'SKIP_CLEANING' || latestEvent.serviceMode === 'NO_SERVICE') {
        candidates.push({
          serviceMode: 'NO_SERVICE',
          priority: SERVICE_MODE_PRIORITY.NO_SERVICE,
          source: 'GREEN_STAY',
          note: 'Guest opted to skip cleaning (Green Stay)',
        });
      } else if (latestEvent.action === 'TOWELS_ONLY' || latestEvent.serviceMode === 'TOWELS_ONLY') {
        candidates.push({
          serviceMode: 'TOWELS_ONLY',
          priority: SERVICE_MODE_PRIORITY.TOWELS_ONLY,
          source: 'GREEN_STAY',
          note: 'Guest requested towels only (Green Stay)',
        });
      }
    }
  }

  // Check for housekeeping requests
  const housekeepingRequest = guestRequests.find(
    r => r.roomNumber === roomNumber &&
         r.category === 'HOUSEKEEPING' &&
         r.status !== 'COMPLETED' &&
         isRequestRelevant(r, targetDate)
  );

  if (housekeepingRequest) {
    candidates.push({
      serviceMode: 'FULL_CLEAN',
      priority: SERVICE_MODE_PRIORITY.FULL_CLEAN,
      source: 'GUEST_REQUEST',
      note: `Guest requested: ${housekeepingRequest.description}`,
    });
  }

  // Default fallback
  if (candidates.length === 0) {
    candidates.push({
      serviceMode: 'FULL_CLEAN',
      priority: SERVICE_MODE_PRIORITY.FULL_CLEAN,
      source: 'DEFAULT',
      note: 'Standard housekeeping',
    });
  }

  // Sort by priority (highest first) and take winner
  candidates.sort((a, b) => b.priority - a.priority);
  const winner = candidates[0]!; // Always has at least one (default)

  console.log(`[HousekeepingPlanner] Room ${roomNumber} DECISION:`, {
    serviceMode: winner.serviceMode,
    source: winner.source,
    priority: winner.priority,
    note: winner.note,
    totalCandidates: candidates.length,
    allCandidates: candidates.map(c => ({ mode: c.serviceMode, source: c.source, priority: c.priority }))
  });

  // Determine task priority
  const taskPriority: TaskPriority =
    winner.serviceMode === 'OUT_OF_ORDER' ? 'HIGH' :
    winner.source === 'GUEST_REQUEST' ? 'HIGH' :
    winner.serviceMode === 'FULL_CLEAN' ? 'MEDIUM' :
    'LOW';

  return {
    roomNumber,
    date: targetDate,
    serviceMode: winner.serviceMode,
    priority: taskPriority,
    source: winner.source,
    notes: candidates.map(c => c.note),
  };
}

/**
 * Check if a request is relevant for the target date
 * Requests are relevant for same day and next day
 */
function isRequestRelevant(request: ApiGuestRequest, targetDate: string): boolean {
  const requestDate = new Date(request.createdAt);
  const target = new Date(targetDate + 'T00:00:00Z');
  const daysDiff = Math.floor((target.getTime() - requestDate.getTime()) / (1000 * 60 * 60 * 24));

  // Request is relevant for 2 days (today and tomorrow)
  return daysDiff >= 0 && daysDiff <= 2;
}

/**
 * Generate housekeeping tasks from room plans
 * Only rooms requiring actual service get tasks
 */
function generateTasksFromPlans(plans: RoomServicePlan[]): HousekeepingTask[] {
  const tasks: HousekeepingTask[] = [];
  let taskCounter = 0;

  for (const plan of plans) {
    // Skip rooms that don't need service
    if (plan.serviceMode === 'NO_SERVICE' || plan.serviceMode === 'OUT_OF_ORDER') {
      continue;
    }

    const taskType = plan.serviceMode === 'TOWELS_ONLY' ? 'TOWELS_ONLY' : 'FULL_CLEAN';

    tasks.push({
      id: `task-${plan.date}-${plan.roomNumber}-${taskCounter++}`,
      roomNumber: plan.roomNumber,
      date: plan.date,
      type: taskType,
      priority: plan.priority,
      source: plan.source,
      estimatedDuration: TASK_DURATION[taskType],
    });
  }

  // Sort tasks by priority (HIGH first)
  tasks.sort((a, b) => {
    const priorityOrder = { HIGH: 3, MEDIUM: 2, LOW: 1 };
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  });

  return tasks;
}

/**
 * Calculate workload summary from room plans
 */
function calculateWorkload(plans: RoomServicePlan[]): WorkloadSummary {
  const workload: WorkloadSummary = {
    fullClean: 0,
    towelsOnly: 0,
    skip: 0,
    outOfOrder: 0,
    checkoutToday: 0,
    total: plans.length,
  };

  for (const plan of plans) {
    switch (plan.serviceMode) {
      case 'FULL_CLEAN':
        workload.fullClean++;
        break;
      case 'TOWELS_ONLY':
        workload.towelsOnly++;
        break;
      case 'NO_SERVICE':
        workload.skip++;
        break;
      case 'OUT_OF_ORDER':
        workload.outOfOrder++;
        break;
      case 'CHECKOUT_TODAY':
        workload.checkoutToday++;
        break;
    }
  }

  return workload;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get today's date in YYYY-MM-DD format
 */
export function getTodayDate(): string {
  const today = new Date();
  const parts = today.toISOString().split('T');
  return parts[0] || '';
}

/**
 * Get tomorrow's date in YYYY-MM-DD format
 */
export function getTomorrowDate(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const parts = tomorrow.toISOString().split('T');
  return parts[0] || '';
}

/**
 * Format date for display
 */
export function formatPlanDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00Z');
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const dateOnly = (d: Date) => d.toISOString().split('T')[0];

  if (dateOnly(date) === dateOnly(today)) {
    return 'Today';
  } else if (dateOnly(date) === dateOnly(tomorrow)) {
    return 'Tomorrow';
  } else {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  }
}

/**
 * Get service mode display label
 */
export function getServiceModeLabel(mode: ServiceMode): string {
  switch (mode) {
    case 'OUT_OF_ORDER':
      return 'Out of Order';
    case 'CHECKOUT_TODAY':
      return 'Checkout Today';
    case 'NO_SERVICE':
      return 'Skip Cleaning';
    case 'TOWELS_ONLY':
      return 'Towels Only';
    case 'FULL_CLEAN':
      return 'Full Clean';
  }
}

/**
 * Get service mode color class
 */
export function getServiceModeColor(mode: ServiceMode): string {
  switch (mode) {
    case 'OUT_OF_ORDER':
      return 'red';
    case 'CHECKOUT_TODAY':
      return 'orange';
    case 'NO_SERVICE':
      return 'gray';
    case 'TOWELS_ONLY':
      return 'amber';
    case 'FULL_CLEAN':
      return 'emerald';
  }
}

/**
 * Estimate total workload duration in minutes
 */
export function estimateTotalDuration(tasks: HousekeepingTask[]): number {
  return tasks.reduce((total, task) => total + task.estimatedDuration, 0);
}

/**
 * Format duration in minutes to human-readable
 */
export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}
